import { useEffect, useRef, useState } from "react";
import flags from "react-phone-number-input/flags";
import { getCountries, getCountryCallingCode } from "react-phone-number-input";
import en from "react-phone-number-input/locale/en.json";

/* ---------- Direction C tokens (styling only) ---------- */
const FONT = "'Plus Jakarta Sans', system-ui, -apple-system, sans-serif";
const MONO = "'IBM Plex Mono', ui-monospace, monospace";
const INK = "#0F1B2A";
const MUTED = "#66748A";
const LINE = "#E4E9EF";
const PRIMARY = "#0B5C8A";

const flagBox = {
  display: "inline-flex",
  width: 20,
  height: 14,
  flex: "0 0 20px",
  overflow: "hidden",
  borderRadius: 3,
  boxShadow: "0 0 0 1px rgba(15,27,42,0.08)",
  alignItems: "center",
  justifyContent: "center",
  fontSize: 12,
  lineHeight: 1,
};

export default function CountrySelect({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const dropdownRef = useRef(null);
  // Menu is positioned against the viewport so it can't be clipped by a
  // parent phone field that uses overflow: hidden for its rounded corners.
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });

  const countries = getCountries();

  const filtered = countries.filter((c) =>
    en[c]?.toLowerCase().includes(search.toLowerCase())
  );

  // ✅ close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (!dropdownRef.current?.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (!open) return;
    const place = () => {
      const r = dropdownRef.current?.getBoundingClientRect();
      if (!r) return;
      const width = 280;
      const left = Math.max(8, Math.min(r.left, window.innerWidth - width - 8));
      const below = window.innerHeight - r.bottom;
      const top = below < 300 && r.top > 300 ? r.top - 6 - 300 : r.bottom + 6;
      setMenuPos({ top, left });
    };
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open]);

  const Flag = flags[value];

  return (
    <div ref={dropdownRef} className="country-container" style={{ position: "relative", display: "flex", alignItems: "center", height: "100%" }}>
      {/* BUTTON */}
      <button
        type="button"
        className="country-btn"
        onClick={(e) => {
          e.stopPropagation(); // 🔥 IMPORTANT
          setOpen((prev) => !prev);
        }}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          height: "100%",
          minHeight: 34,
          padding: "0 8px",
          margin: 0,
          background: "transparent",
          border: 0,
          borderRadius: 8,
          cursor: "pointer",
          fontFamily: MONO,
          fontSize: 13,
          fontWeight: 600,
          color: INK,
        }}
      >
        {/* FLAG */}
        <span className="flag-box" style={flagBox}>
          {Flag ? <Flag /> : "🌐"}
        </span>

        {/* CODE */}
        <span className="country-code">
          +{getCountryCallingCode(value)}
        </span>

        {/* SVG ARROW */}
        <svg
          className={`arrow ${open ? "rotate" : ""}`}
          width="10"
          height="10"
          viewBox="0 0 10 6"
          style={{ color: open ? PRIMARY : MUTED, transform: open ? "rotate(180deg)" : "none", transition: "transform 0.15s" }}
        >
          <path
            d="M1 1l4 4 4-4"
            stroke="currentColor"
            strokeWidth="1.5"
            fill="none"
          />
        </svg>
      </button>

      {/* DROPDOWN */}
      {open && (
        <div
          className="country-dropdown"
          style={{
            position: "fixed",
            top: menuPos.top,
            left: menuPos.left,
            width: 280,
            maxHeight: 300,
            display: "flex",
            flexDirection: "column",
            zIndex: 1500,
            background: "#FFFFFF",
            border: `1px solid ${LINE}`,
            borderRadius: 12,
            boxShadow: "0 16px 36px -12px rgba(15,27,42,0.28)",
            overflow: "hidden",
            fontFamily: FONT,
          }}
        >
          <div className="search-box" style={{ padding: 8, borderBottom: "1px solid #EDF1F5" }}>
            <input
              type="text"
              placeholder="Search country..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoFocus
              style={{
                display: "block",
                width: "100%",
                boxSizing: "border-box",
                height: 36,
                padding: "0 10px",
                margin: 0,
                background: "#F7F9FC",
                border: "1px solid #D5DEE7",
                borderRadius: 9,
                fontFamily: FONT,
                fontSize: 13.5,
                color: INK,
                outline: "none",
              }}
            />
          </div>

          <ul className="country-list" style={{ listStyle: "none", margin: 0, padding: 4, overflowY: "auto", flex: 1 }}>
            {filtered.map((c) => {
              const FlagComp = flags[c];
              const on = c === value;
              return (
                <li key={c}>
                  <button
                    type="button"
                    className="country-item"
                    onClick={() => {
                      onChange(c);
                      setOpen(false);
                      setSearch("");
                    }}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      width: "100%",
                      minHeight: 36,
                      padding: "6px 10px",
                      margin: 0,
                      background: on ? "#EAF2FB" : "transparent",
                      border: 0,
                      borderRadius: 8,
                      cursor: "pointer",
                      textAlign: "left",
                      fontFamily: FONT,
                      fontSize: 13.5,
                      fontWeight: on ? 700 : 500,
                      color: on ? "#1B4F82" : INK,
                    }}
                    onMouseEnter={(e) => { if (!on) e.currentTarget.style.background = "#F4F7FA"; }}
                    onMouseLeave={(e) => { if (!on) e.currentTarget.style.background = "transparent"; }}
                  >
                    <span className="flag-box" style={flagBox}>
                      {FlagComp && <FlagComp />}
                    </span>

                    <span className="country-name" style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {en[c]}
                    </span>

                    <span className="country-code-small" style={{ flex: "0 0 auto", fontFamily: MONO, fontSize: 12, color: MUTED }}>
                      +{getCountryCallingCode(c)}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
